import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Input, message, InputNumber, Checkbox } from "antd";
import { all_routes } from "../../../router/all_routes";
import { parseSQLDate, formatToSQLDate } from "../../../utils/dateUtils";
import { GENDER_OPTIONS, CITIES_OPTIONS } from "../../../constants/userConstants";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import { isValidCIN, isValidPhoneNumber } from "utils/validators";
import CommonSelectLite from "components/Common/CommonSelectLite";
import { CONTRACT_TYPE_OPTIONS } from "constants/staffConstants";
import { useAbility } from "config/abilityContext";
import CommonFormLayout from "components/Common/CommonFormLayout";
import CommonSelect from "components/Common/CommonSelect";
import useReference from "hooks/useReference";
import useStaff from "../hooks/useStaff";



const StaffFormPage = () => {
  const { t } = useTranslation();
  const routes = all_routes;
  const { id: managerId } = useParams<{ id: string }>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const contract = Form.useWatch(['staff', 'contract'], form) ?? null;
  const translatedGender = GENDER_OPTIONS.map((gender) => ({
    value: gender.value,
    label: t(gender.label),
  }));
  const { handleGetStaffById, handleCreate, handleUpdate } = useStaff()
  const { jobPositions, fetchJobPositions } = useReference()

  useEffect(() => {
    fetchJobPositions();

    if (managerId) {
      setIsEdit(true);
      fetchStaffData(managerId);
    }
  }, [managerId]);


  const jobPositionOptions = useMemo(
    () => jobPositions?.map((jobPosition) => ({
      value: jobPosition.id,
      label: t("job_positions." + jobPosition.code),
    })),
    [jobPositions]
  );

  const fetchStaffData = async (id: string) => {
    try {
      const staffResponse = await handleGetStaffById(id);
      if (!staffResponse) return false;
      const initialData = {
        user: {
          firstName: staffResponse?.firstName || "",
          lastName: staffResponse?.lastName || "",
          email: staffResponse?.email || "",
          phoneNumber: staffResponse?.phoneNumber || "",
          birthDate: parseSQLDate(staffResponse?.birthDate),
          gender: staffResponse?.gender || null,
          cityOfBirth: CITIES_OPTIONS.find((city) => city.name === staffResponse?.cityOfBirth)?.name || "",
          zipCode: staffResponse?.zipCode || "",
          address: staffResponse?.address || "",
          city: CITIES_OPTIONS.find((city) => city.name === staffResponse?.city)?.name || "",
          roleId: staffResponse?.roles?.[0]?.id || null,
          role: staffResponse?.roles?.[0]?.slug == "director" ? "director" : "manager",
        },
        staff: {
          contract: staffResponse.staff?.contract || "",
          startDate: parseSQLDate(staffResponse.staff?.startDate),
          endDate: parseSQLDate(staffResponse.staff?.endDate),
          monthlySalary: Number(staffResponse.staff?.monthlySalary) || 0,
          cinNumber: staffResponse.staff?.cinNumber || null,
          cnssNumber: staffResponse.staff?.cnssNumber || null,
          matricule: staffResponse.staff?.matricule || null,
          jobPositionId: staffResponse.staff?.jobPositionId || null,

        },
      };

      form.setFieldsValue(initialData);
    } catch (error) {
      console.log("error", error)
      message.error(t("failed_to_load_teacher_data"));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const staffResponse = {
        ...values,
        user: {
          ...values.user,
          birthDate: values.user.birthDate
            ? formatToSQLDate(values.user.birthDate)
            : null,
        },
        staff: values?.staff ? {
          ...values.staff,
          startDate: values?.staff?.startDate
            ? formatToSQLDate(values.staff.startDate)
            : null,
          endDate: values.staff.endDate
            ? formatToSQLDate(values.staff.endDate)
            : null,

        } : null,
      };
      let response = false;
      if (isEdit) {
        response = await handleUpdate(managerId!, staffResponse);
      } else {
        response = await handleCreate(staffResponse);
      }

      if (response) {
        navigate(routes.staff);
      }
    } catch (error) {
      console.log("err", error)
    }
  };

  const sections = [
    {
      title: t("personal_information"),
      content: (
        <div className="row">
          <div className="col-md-4">
            <Form.Item
              label={t("first_name")}
              name={["user", "firstName"]}
              rules={[{ required: true, message: t("validation.firstName_required") }]}
            >
              <Input placeholder={t("enter_first_name")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("last_name")}
              name={["user", "lastName"]}
              rules={[{ required: true, message: t("validation.lastName_required") }]}
            >
              <Input placeholder={t("enter_last_name")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("gender")}
              name={["user", "gender"]}
              rules={[{ required: true, message: t("gender_required") }]}
            >
              <CommonSelect
                options={translatedGender}
                defaultValue={translatedGender.find(
                  (opt) => opt.value === form.getFieldValue(["user", "gender"])
                )}
                onChange={(option) =>
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue(),
                      gender: option?.value ?? "",
                    },
                  })
                }
              />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("email_address")}
              name={["user", "email"]}
              rules={
                [
                  { required: true, message: t("email_required") },
                  { type: "email", message: t("validation.please_enter_valid_email") },
                ]}
            >
              <Input placeholder={t("enter_email")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("phone_number")}
              name={["user", "phoneNumber"]}
              rules={[{ required: true, message: t("validation.phoneNumber_required") }, {
                validator: (_, value) =>
                  value && !isValidPhoneNumber(value)
                    ? Promise.reject(t("validation.phone_number_invalid"))
                    : Promise.resolve(),
              },]}
            >
              <Input placeholder={t("enter_phone_number")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("birth_date")}
              name={["user", "birthDate"]}
              rules={[{ required: true, message: t("validation.birthdate_required") }]}
            >
              <LocalizedDatePicker className="form-control datetimepicker" pastOnly />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("city_of_birth")}
              name={["user", "cityOfBirth"]}
              rules={[{ required: true, message: t("validation.cityOfBirth_required") }]}
            >
              {/* <Input placeholder={t("enter_city_of_birth")} /> */}
              <CommonSelectLite
                options={CITIES_OPTIONS}
                optionValueKey="name"
                optionLabelKey="name"
                value={form.getFieldValue(["user", "cityOfBirth"]) || "casablanca"}
                placeholder={t("enter_city_of_birth")}
                onChange={(value) =>
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue(),
                      cityOfBirth: value,
                    },
                  })
                }
              />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("address")}
              name={["user", "address"]}
              rules={[{ required: true, message: t("validation.address_required") }]}
            >
              <Input placeholder={t("enter_address")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("zip_code")}
              name={["user", "zipCode"]}
              rules={[{ required: true, message: t("validation.zipCode_required") }]}
            >
              <Input placeholder={t("enter_zip_code")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              label={t("city")}
              name={["user", "city"]}
              rules={[{ required: true, message: t("validation.city_required") }]}
            >
              {/* <Input placeholder={t("enter_city")} /> */}
              <CommonSelectLite
                options={CITIES_OPTIONS}
                optionValueKey="name"
                optionLabelKey="name"
                placeholder={t("enter_city")}
                value={form.getFieldValue(["user", "city"])}
                onChange={(value) =>
                  form.setFieldsValue({
                    user: {
                      ...form.getFieldsValue(),
                      city: value,
                    },
                  })
                }
              />
            </Form.Item>
          </div>
        </div>
      ),
    },
    {
      title: t("staff.info_contract"),
      content: (
        <>


          <div className="row">
            <div className="col-md-4">
              <Form.Item
                label={t("staff.form.contractType")}
                name={["staff", "contract"]}
                rules={[{ required: true, message: t("staff.validation.contractType_required") }]}
              >
                <CommonSelectLite
                  options={CONTRACT_TYPE_OPTIONS}
                  optionValueKey="value"
                  optionLabelKey="label"
                  placeholder={t("staff.form.contractType")}
                  value={form.getFieldValue("staff.contract")}
                  onChange={(value) =>
                    form.setFieldsValue({
                      staff: {
                        ...form.getFieldsValue().staff,
                        contract: value,
                      },
                    })
                  }
                />
              </Form.Item>
            </div>

            <div className="col-md-4">
              <Form.Item
                label={t("staff.form.startDate")}
                name={["staff", "startDate"]}
                rules={[{ required: true, message: t("staff.validation.startDate_required") }]}
              >
                <LocalizedDatePicker className="form-control datetimepicker" />
              </Form.Item>
            </div>
            {contract !== "CDI" && (
              <div className="col-md-4">
                <Form.Item
                  label={t("staff.form.endDate")}
                  name={["staff", "endDate"]}
                  rules={[{ required: true, message: t("staff.validation.endDate_required") },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue(["staff", "startDate"]);
                      if (!value || !startDate) return Promise.resolve();
                      if (value.isAfter(startDate, "day")) return Promise.resolve();
                      return Promise.reject(
                        new Error(t("staff.validation.endDate_must_be_after_start_date"))
                      );
                    },
                  }),
                  ]}
                >
                  <LocalizedDatePicker className="form-control datetimepicker" />
                </Form.Item>
              </div>
            )}
            <div className="col-md-4">
              <Form.Item
                label={t("staff.form.monthlySalary")}
                name={["staff", "monthlySalary"]}
                rules={[{ required: true, message: t("staff.validation.monthlySalary_required") }]}
              >
                <InputNumber
                  placeholder={t("staff.placeholders.enter_monthly_salary")}
                  min={0}
                  precision={2}
                  className="d-flex"
                  addonAfter="MAD"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item
                label={t("staff.form.cinNumber")}
                name={["staff", "cinNumber"]}
                rules={[{ required: true, message: t("staff.validation.cinNumber_required") }, {
                  validator: (_, value) =>
                    value && !isValidCIN(value)
                      ? Promise.reject(t("staff.validation.cinNumber_invalid"))
                      : Promise.resolve(),
                }]}
              >
                <Input
                  placeholder={t("staff.placeholders.enter_cinNumber")}
                />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item
                label={t("staff.form.cnssNumber")}
                name={["staff", "cnssNumber"]}
              >
                <Input
                  placeholder={t("staff.placeholders.enter_cnssNumber")}
                />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item
                label={t("staff.form.matricule")}
                name={["staff", "matricule"]}
              >
                <Input
                  placeholder={t("staff.placeholders.enter_matricule")}
                />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item
                label={t("staff.form.jobPosition")}
                name={["staff", "jobPositionId"]}
                rules={[{ required: true, message: t("staff.validation.jobPosition_required") }]}
              >
                <CommonSelect
                  options={jobPositionOptions}
                  defaultValue={jobPositionOptions.find(
                    (opt) => opt.value === form.getFieldValue(["staff", "jobPositionId"])
                  )}
                  onChange={(option) =>
                    form.setFieldsValue({
                      staff: {
                        ...form.getFieldsValue(),
                        jobPositionId: option?.value ?? "",
                      },
                    })
                  }
                />
              </Form.Item>
            </div>
          </div>

        </>
      ),
    },
  ];

  return <CommonFormLayout title={isEdit ? t("staff.edit_staff") : t("staff.add_staff")} form={form} onFinish={handleSubmit} sections={sections} cancelRoute={routes.userManagement} submitText={t("add")} isEdit={isEdit} />;
};

export default StaffFormPage;
